/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {getEnvironment} from '../../utils/Environment';
import DocumentAccessService from './DocumentAccessService';
import AppHomeAnalytics from '../../utils/AppHomeAnalytics';
class CCProjectsClientService {
	static instance: CCProjectsClientService;
	ccProjectsClient: any;

	/**
	 * Retrieve cc projects client or initialize cc projects client if unable to retrieve
	 * @returns {Promise<any | Error>}
	 */
	async getCCProjectsClient(): Promise<any | Error> {
		if (!this.ccProjectsClient) {
			const projectsClient = await this._initializeCCProjectsClient();
			if (!(projectsClient instanceof Error)) {
				this.ccProjectsClient = projectsClient;
				return projectsClient;
			}
				const CCProjectsClientServiceError = new Error('getCCProjectsClient error');
				throw CCProjectsClientServiceError;

		}
		else {
			return this.ccProjectsClient;
		}
	}

	/**
	 * Create new canvas flow, uses ccprojects to open the uap model. If the user has a space, creates a
	 * new document in said space, otherwise initializes a new default space and redirects user
	 * @param defaultSpaceName on default space creation, name of the space to be created
	 * @returns {Promise<any>}
	 */
	async createCanvas(defaultSpaceName: string): Promise<any> {
		const appHomeAnalytics = new AppHomeAnalytics();
		const projectsClient = await this.getCCProjectsClient();
		const canvasResponse = await projectsClient.selectSpaceForCanvas();
		if (canvasResponse instanceof Error) {
			appHomeAnalytics.postCreateNewCanvasSuccess(false, '', '');
			window.cchome.util.logger.log('CCProjectsService', 'Error creating new canvas', canvasResponse);
			const CCProjectsClientServiceError = new Error('createCanvas error');
			throw CCProjectsClientServiceError;
		}
		if (canvasResponse && (canvasResponse.spaceData || canvasResponse.teamData)) {
			let newDocument;
			if (canvasResponse.spaceData) {
				// Case: space exists
				const repoId
					= canvasResponse.spaceData && canvasResponse.spaceData['repo:repositoryId'];
				const assetId = canvasResponse.spaceData && canvasResponse.spaceData['repo:assetId'];
				newDocument = await new DocumentAccessService().createNewDocumentInSpace(
					assetId,
					repoId,
				);
			}
			else if (canvasResponse.teamData) {
				// Case: space does not exist, create a default space
				const teamAssetId = canvasResponse.teamData && canvasResponse.teamData['repo:assetId'];
				const repoName = defaultSpaceName;
				newDocument = await new DocumentAccessService().createNewSpace(teamAssetId, repoName);
			}

			if (newDocument instanceof Error) {
				window.cchome.util.logger.log('CCProjectsService', 'Error creating new canvas', newDocument);
				const CCProjectsClientServiceError = new Error('createCanvas error');
				throw CCProjectsClientServiceError;
			}
			else {
				// get id of new canvas for analytics
				const match = new RegExp('[^/]+(?=/$|$)').exec(newDocument);
				appHomeAnalytics.postCreateNewCanvasSuccess(true, match && match[0], newDocument);

				// if adequate response, redirect using the link provided
				open(newDocument, '_self');
				window.cchome.util.logger.log('CCProjectsService', 'createCanvas', newDocument);
			}
		}
	}

	/**
	 * Create new template copy workflow, on a given document urn, creates a copy of the document
	 * into a space of the user's choosing with the given template title
	 * @param sourceDocId urn for the document to be copied
	 * @param templateTitle title for the newly created document
	 * @param defaultSpaceName on default space creation, name of the space to be created
	 * @returns {Promise<any | Error>}
	 */
	async createTemplateCopy(sourceDocId: string, templateTitle: string, defaultSpaceName: string): Promise<any | Error> {
		const projectsClient = await this.getCCProjectsClient();
		const spaceResponse = await projectsClient.selectSpaceForCanvas();
		let newTemplate;

		if (spaceResponse && (spaceResponse.teamData && !spaceResponse.spaceData)) {
			// The user has a team but not a space to copy the document into, we need to create a space for them
			const teamAssetId = spaceResponse.teamData && spaceResponse.teamData['repo:assetId'];
			const repoName = defaultSpaceName;
			newTemplate = await new DocumentAccessService().copyDocumentIntoNewSpace(
				sourceDocId,
				`${templateTitle}`,
				teamAssetId,
				repoName,
			);
		}
		else if (spaceResponse && spaceResponse.spaceData) {
			// user chose a space
			const repoId
				= spaceResponse.spaceData && spaceResponse.spaceData['repo:repositoryId'];
			const assetId = spaceResponse.spaceData && spaceResponse.spaceData['repo:assetId'];
			newTemplate = await new DocumentAccessService().copyDocumentIntoSpace(
				sourceDocId,
				`${templateTitle}`,
				repoId,
				assetId,
			);
		}

		if (newTemplate instanceof Error) {
			window.cchome.util.logger.log('CCProjectsService', 'Error creating new template', newTemplate);
			const CCProjectsClientServiceError = new Error('createTemplateCopy error');
			throw CCProjectsClientServiceError;
		}
		else if (newTemplate) {
			open(newTemplate, '_self');
			window.cchome.util.logger.log('AppHome', 'newTemplate response', newTemplate);
		}
	}

	async userHasCanvasEntitlement() {
		const projectsClient = await this.getCCProjectsClient();
		return await projectsClient.checkCanvasCapability();
	}

	/*
	**
	**  P R I V A T E   M E T H O D S
	**
	*/
	constructor() {
		if (!CCProjectsClientService.instance) {
			CCProjectsClientService.instance = this;
		}
		return CCProjectsClientService.instance;
	}

	/**
	 * Initialize cc projects client with given IMS data
	 * @returns {Promise<any>}
	 */
	_initializeCCProjectsClient(): Promise<any> {
		return new Promise((resolve) => {
			const IMSData = {
				accountType: window.adobeIMS?.getUserProfile()?.account_type,
				displayName: window.adobeIMS?.getUserProfile()?.displayName,
				environment: getEnvironment() !== 'prod' ? 'stage' : 'prod',
				subscriptionType: window.adobeIMS?.getUserProfile()?.serviceAccounts && window.adobeIMS?.getUserProfile()?.serviceAccounts[0]?.serviceLevel || '',
				serviceLevel: window.adobeIMS?.getUserProfile()?.serviceAccounts && window.adobeIMS?.getUserProfile()?.serviceAccounts[0]?.serviceLevel || '',
				token: window.adobeIMS?.getAccessToken(),
				userEmail: window.adobeIMS?.getUserProfile()?.email,
				userId: window.adobeIMS?.getUserProfile()?.userId,
			};
			const locale = window.cchome?.util?.getLocale();
			import(/* webpackChunkName: "ccProjects" */ '@ccx/projects')
				.then((ccProjectsLib) => {
					import(/* webpackChunkName: "documentPicker" */ '@ccx/ccx-document-picker').then(
						(ccxDocumentPickerLib) => {
							ccProjectsLib.sharedStore.updateStore({imsData: IMSData, systemLocale: locale, locale}).then(() => {
								const showPicker = ccxDocumentPickerLib.showPicker;
								try {
									resolve(new ccProjectsLib.ProjectsClient({
										locale: 'en',
										showPicker,
									}));
								}
								catch (e) {
									window.cchome.util.logger.log(
										'CCProjectsClientService',
										'Error initializing ccProjectsClient',
										e,
									);
									const CCProjectsClientServiceError = new Error('_initializeCCProjectsClient error');
									resolve(CCProjectsClientServiceError);
								}
							});
						},
					);
				})
				.catch((e) => {
					console.error(e);
					const CCProjectsClientServiceError = new Error('_initializeCCProjectsClient error');
					resolve(CCProjectsClientServiceError);
				});
		});
	}
}

export default CCProjectsClientService;
